import { useCallback, useEffect, useMemo } from 'react'
import { NoteCellLinkSource, NoteCellLinkStatus } from '@common/interfaces/noteCellLink'
import {
  confirmCellNoteLink,
  dismissCellNoteLink,
  listCellNoteLinks,
} from '../redux/cellNotes/actions'
import {
  getCellAuthoredNotesForCell,
  getCellNoteLinksForCell,
  getCellNoteLinksLoadingForCell,
} from '../redux/cellNotes/selectors'
import { useDispatch, useSelector } from '../redux/hooks'

interface UseCellNoteLinksConfig {
  tableType: string
  /** Which client's indicator cache a decision on this cell should move. */
  clientId: string
  workflow: string
  id: string
  rowId: string
  columnId: string
  /** Columns this cell rolls up (quarterly/annual views). Defaults to columnId alone. */
  columnIds?: string[]
  recordDate?: string
  enabled?: boolean
}

/**
 * Loads everything attributed to a single focused cell, powering the cell context panel.
 * Splits links into established facts (amendment/rule — ground truth), confirmed notes and
 * pending suggestions, and exposes one-click confirm/dismiss.
 *
 * `authoredNotes` are the notes actually written on the cell, which are NOT links — a cell
 * can hold a real note and a pending suggestion at once, and the panel has to show both.
 */
export const useCellNoteLinks = ({
  tableType,
  clientId,
  workflow,
  id,
  rowId,
  columnId,
  columnIds,
  recordDate,
  enabled = true,
}: UseCellNoteLinksConfig) => {
  const dispatch = useDispatch()

  const links = useSelector(getCellNoteLinksForCell(tableType, rowId, columnId, recordDate))
  const authoredNotes = useSelector(
    getCellAuthoredNotesForCell(tableType, rowId, columnId, recordDate),
  )
  const isLoading = useSelector(
    getCellNoteLinksLoadingForCell(tableType, rowId, columnId, recordDate),
  )

  useEffect(() => {
    // No coordinate guard: '' is a valid coordinate, and rowId='' + columnId='' addresses
    // table scope — where facility-wide established facts live. `enabled` is the gate.
    if (enabled && id && tableType) {
      dispatch(
        listCellNoteLinks({ tableType, workflow, id, rowId, columnId, columnIds, recordDate }),
      )
    }
    // columnIds is derived from columnId + the table's period view, so it never changes
    // independently — depending on columnId keeps the effect from re-firing on a new array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, enabled, id, tableType, workflow, rowId, columnId, recordDate])

  const { established, confirmed, suggested } = useMemo(() => {
    const isEstablished = (source: NoteCellLinkSource) =>
      source === NoteCellLinkSource.Amendment || source === NoteCellLinkSource.Rule
    return {
      established: links.filter((link) => isEstablished(link.source)),
      confirmed: links.filter(
        (link) => !isEstablished(link.source) && link.status === NoteCellLinkStatus.Confirmed,
      ),
      suggested: links.filter(
        (link) => !isEstablished(link.source) && link.status === NoteCellLinkStatus.Suggested,
      ),
    }
  }, [links])

  // The mutation response identifies the link but not the client — `clientName` on a link is
  // only ever set for note-less established facts, and a decision can only act on a parsed
  // one. So the caller tags the decision with the table it was made in; without that the
  // reducer cannot tell which client's cached marker to move.
  const confirm = useCallback(
    (linkId: string) => {
      dispatch(confirmCellNoteLink(linkId, { tableType, clientId }))
    },
    [dispatch, tableType, clientId],
  )
  const dismiss = useCallback(
    (linkId: string) => {
      dispatch(dismissCellNoteLink(linkId, { tableType, clientId }))
    },
    [dispatch, tableType, clientId],
  )

  return { links, authoredNotes, established, confirmed, suggested, isLoading, confirm, dismiss }
}

export default useCellNoteLinks
